import { getAddress, isAddress, isAddressEqual, zeroAddress } from 'viem'
import { scanAddressPath } from '@legacy-ui/utils'
import { type Address } from '@primitives/address.utils'

type ShortenAddressOptions = {
  /** Number of digits to show on each side of the shortened address (default: 4) */
  digits?: number
}

/**
 * Shortens a hash (e.g., transaction hash or address) by displaying the first and last few characters
 */
export const shortenHash = (hash: string, { digits = 5 }: ShortenAddressOptions = {}) =>
  `${hash.slice(0, digits + 2)}...${hash.slice(-digits)}`

/**
 * Shortens an Ethereum address by displaying first and last few characters
 *
 * @param address - Ethereum address to shorten
 * @param options - Configuration options for shortening
 * @returns Shortened address string in format for example "0xAB...XY"
 * @example
 * // With default parameters (4 digits)
 * shortenAddress("0x1234567890abcdef1234567890abcdef12345678") // "0x1234...5678"
 *
 * // With 6 digits on each side
 * shortenAddress("0x1234567890abcdef1234567890abcdef12345678", { digits: 6 }) // "0x123456...345678"
 *
 * @remarks
 * This function explicitly applies checksumming to the address for consistency.
 * We actively discourage non-checksummed addresses and therefore don't provide an option to bypass it.
 * The checksumming is applied as a precaution since we don't control the input address string.
 * The `getAddress` function from viem applies checksumming to the address and has a checksum cache
 * to optimize performance when the same address is used multiple times.
 * To enforce consistency, there's no option to include the 0x prefix on the starting digits length.
 */
export const shortenAddress = (address: string | undefined, options?: ShortenAddressOptions): string =>
  shortenHash(getAddress(address || zeroAddress), options)

/** Makes sure a list of addresses doesn't contain duplicates. */
export const uniqAddresses = (addresses: Address[]) => Array.from(new Set(addresses))

export const isValidAddress = (address: string) => isAddress(address) && !isAddressEqual(address, zeroAddress)

export const THREECRV_ADDRESS = '0x6c3F90f043a72FA612cbac8115EE7e52BDe6E490' as const
export const CRVUSD_ADDRESS = '0xf939e0a03fb07f59a73314e73794be0e57ac1b4e' as const
export const MAINNET_CRV_ADDRESS = '0xd533a949740bb3306d119cc777fa900ba034cd52' as const
export const REUSD_ADDRESS = '0x57aB1E0003F623289CD798B1824Be09a793e4Bec' as const
export const SREUSD_ADDRESS = '0x557AB1e003951A73c12D16F0fEA8490E39C33C35' as const

export const ARC_USDC_ADDRESS = '0x3600000000000000000000000000000000000000' as const

export const ARAGON_OWNERSHIP_VOTING = '0xe478de485ad2fe566d49342cbd03e49ed7db3356' as const
export const ARAGON_OWNERSHIP_AGENT = '0x40907540d8a6c65c637785e8f8b742ae6b0b9968' as const
export const GAUGE_CONTROLLER = '0x2f50d538606fa9edd2b11e2446beb18c9d5846bb' as const

export const CRVUSD = {
  symbol: 'crvUSD',
  address: CRVUSD_ADDRESS,
  decimals: 18,
  name: 'crvUSD',
  blockchainId: 'ethereum',
} as const

export const MAINNET_CRV = {
  symbol: 'CRV',
  address: MAINNET_CRV_ADDRESS,
  decimals: 18,
  name: 'CRV',
  blockchainId: 'ethereum',
} as const

export const evmAddressDisplay = { shortenAddress, scanAddressPath }
